import { COUNTRY } from '../../../utils/constants'
import {
  anchorageOptions,
  FOUNDATION_TYPE,
  GEOMETRY_TYPE,
  HOLDOWN_SUBTYPE,
  HOLDOWN_TYPE,
  LOAD_TYPE_CANADA,
  MATERIAL_TYPE,
  POST_BASE_SUBTYPE,
  postBaseFastenersTypeOptions,
  postBaseLumberSpeciesOptions,
  postBaseMaterialTypeOptions,
} from './constants'
import {
  getGeometrySubTypeOptions,
  getGeometryTypeOptions,
  getHoldownLumberSpeciesOptions,
  getMinStemwallThicknessOptions,
  getPostThicknessOptions,
  getPostWidthOptions,
  getTextFromOptions,
} from './fns'
import {
  compressiveStrengthData,
  corrosionResistanceOptions,
  designCodeData,
  downloadDurationData,
  foundationTypeData,
  holdownFastenersTypeData,
  installationTypeData,
  installOverSheathingData,
  loadTypeData,
  materialTypeData,
  postBaseGeometrySubTypeData,
  postBaseGeometryTypeData,
  postBaseLoadTypeData,
  postTypeData,
  raisedFloorData,
  twoPourInstallData,
  upliftDurationData,
  wallThicknessData,
} from './json/staticData.json'

const arrayToObject = (array, keyField) =>
  array.reduce((obj, item) => {
    obj[item[keyField]] = item
    return obj
  }, {})

const getLabel = (options, value, name) => {
  // console.log('---name,value---', name, ',', value)
  if (!value) {
    value = 'null'
  }
  const x = arrayToObject(options, 'value')
  if (x && x[value] != undefined && x[value]) {
    return x[value].text
  } else {
    // console.log('---name,value---', name, ',', value, ',', options)
    return 'N/A'
  }
}

export const formatCommonParameters = (source) => {
  let {
    loadType,
    designCode,
    holdownType,
    holdownSubType,
    lumberSpecies,
    wallThickness,
    installOverSheathing,
    holdownFastenersType,
    compressiveStrength,
    anchorageOption,
    raisedFloor,
    materialType,
    twoPourInstall,
    geometryType,
    geometrySubType,
    postBaseLoadType,
    postBaseSubType,
    postBaseFastenersType,
    postBaseLumberSpecies,
    postBaseMaterialType,
    postBaseGeometryType,
    postBaseGeometrySubType,
    corrosionResistance,
    upliftLoadASD,
    downloadASD,
    lateralF1,
    lateralF2,
    settings: { country },
    upliftDuration,
    factoredUpliftLRFD,
    postWidth,
    postThickness,
    holdownDesign,
    minStemwallThickness,
    foundationType,
    installationType,
    downloadDuration,
    factoredDownloadLRFD,
    postBaseType,
  } = source

  return {
    settingsCountry: country,
    upliftLoadASD: upliftLoadASD,
    downloadASD: downloadASD,
    lateralF1: lateralF1,
    lateralF2: (() => {
      return country === COUNTRY.USA ||
        (country === COUNTRY.CANADA && foundationType !== FOUNDATION_TYPE.WOOD)
        ? lateralF2
        : 'N/A'
    })(),
    loadType: (() => {
      if (country === COUNTRY.USA) {
        return getLabel(loadTypeData, loadType)
      }
      if (country === COUNTRY.CANADA && holdownType === HOLDOWN_TYPE.CAST_IN_PLACE) {
        return getLabel(LOAD_TYPE_CANADA, loadType)
      }
      return 'N/A'
    })(),
    designCode: getLabel(designCodeData, designCode, 'designCode'),
    holdownSubType:
      holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_ANCHOR
        ? 'Embedded Anchor'
        : holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_STRAP
        ? 'Embedded Strap'
        : 'N/A',
    lumberSpecies: getLabel(
      getHoldownLumberSpeciesOptions(country, holdownType),
      lumberSpecies,
      'lumberSpecies',
    ),
    wallThickness: getLabel(wallThicknessData, wallThickness, 'wallThickness'),
    installOverSheathing:
      holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_STRAP
        ? getLabel(installOverSheathingData, installOverSheathing, 'installOverSheathing')
        : 'N/A',
    holdownFastenersType: holdownFastenersType
      .map((val) => getTextFromOptions(holdownFastenersTypeData, val))
      .join(', '),
    compressiveStrength:
      materialType === MATERIAL_TYPE.CONCRETE
        ? getLabel(compressiveStrengthData.options, compressiveStrength, 'compressiveStrength')
        : 'N/A',
    materialType: getLabel(
      materialTypeData.holdownSubType.embeddedAnchorOptions,
      materialType,
      'materialType',
    ),
    anchorageOption:
      holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_ANCHOR
        ? getLabel(anchorageOptions[materialType], anchorageOption, 'anchorageOption')
        : 'N/A',
    raisedFloor:
      holdownSubType === HOLDOWN_SUBTYPE.EMBEDDED_STRAP
        ? getLabel(raisedFloorData, raisedFloor, 'raisedFloor')
        : 'N/A',
    twoPourInstall: getLabel(twoPourInstallData, twoPourInstall, 'twoPourInstall'),
    geometryType: getLabel(
      getGeometryTypeOptions(materialType, anchorageOption).options,
      geometryType,
      'geometryType',
    ),
    geometrySubType:
      geometryType !== GEOMETRY_TYPE.ELEVATED_SLAB &&
      geometryType !== GEOMETRY_TYPE.INTERIOR_THICKENED_FOOTING
        ? getLabel(
            getGeometrySubTypeOptions(geometryType, materialType, anchorageOption).options,
            geometrySubType,
            'geometrySubType',
          )
        : 'N/A',
    postBaseFastenersType: postBaseFastenersType
      .map((val) =>
        getTextFromOptions(
          postBaseFastenersTypeOptions[
            country === COUNTRY.USA ? postBaseSubType : POST_BASE_SUBTYPE.POST_INSTALLED
          ],
          val,
        ),
      )
      .join(', '),
    postBaseLoadType: getLabel(postBaseLoadTypeData, postBaseLoadType),
    postBaseSubType:
      postBaseSubType === POST_BASE_SUBTYPE.POST_INSTALLED
        ? 'Post-Installed'
        : postBaseSubType === POST_BASE_SUBTYPE.CAST_IN_PLACE
        ? 'Cast-In-Place'
        : 'N/A',
    postBaseLumberSpecies: getLabel(
      postBaseLumberSpeciesOptions[country][
        country === COUNTRY.USA ? postBaseSubType : postBaseType
      ],
      postBaseLumberSpecies,
    ),
    postBaseMaterialType: getLabel(
      postBaseMaterialTypeOptions[postBaseSubType],
      postBaseMaterialType,
    ),
    postBaseGeometryType: getLabel(postBaseGeometryTypeData, postBaseGeometryType),
    postBaseGeometrySubType: getLabel(postBaseGeometrySubTypeData, postBaseGeometrySubType),
    corrosionResistance: corrosionResistance
      .map((val) => getTextFromOptions(corrosionResistanceOptions, val))
      .join(', '),
    upliftDuration: getLabel(upliftDurationData, upliftDuration),
    factoredUpliftLRFD: factoredUpliftLRFD,
    postWidth: getLabel(
      getPostWidthOptions(holdownDesign, holdownDesign ? lumberSpecies : postBaseLumberSpecies),
      postWidth,
    ),
    postThickness: getLabel(
      getPostThicknessOptions(holdownDesign, holdownDesign ? lumberSpecies : postBaseLumberSpecies),
      postThickness,
    ),
    stemwallThickness: getLabel(
      getMinStemwallThicknessOptions(holdownDesign, foundationType),
      minStemwallThickness,
    ),
    holdownType: getLabel(postTypeData, holdownType),
    installationType:
      holdownType === HOLDOWN_TYPE.CAST_IN_PLACE
        ? getLabel(installationTypeData, installationType)
        : 'N/A',
    downloadDuration:
      foundationType !== FOUNDATION_TYPE.WOOD
        ? getLabel(downloadDurationData, downloadDuration)
        : 'N/A',
    factoredDownloadLRFD: foundationType !== FOUNDATION_TYPE.WOOD ? factoredDownloadLRFD : 'N/A',
    postBaseType: getLabel(postTypeData, postBaseType),
    foundationType: getLabel(foundationTypeData, foundationType),
  }
}
